import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, serverTimestamp, doc, setDoc, getDocs, deleteDoc, addDoc } from "firebase/firestore";

export default function AdminBlogs() {
    const [formData, setFormData] = useState({
        title: "",
        category: "Being You",
        author: "",
        date: "",
        content: ""
    });
    const [headerImage, setHeaderImage] = useState(null);
    const [embeddedImage, setEmbeddedImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [blogs, setBlogs] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const storage = getStorage();

    const fetchBlogs = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "blogs"));
            const fetched = querySnapshot.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
            // sort by timestamp or date if possible, but for now just set
            setBlogs(fetched);
        } catch (error) {
            console.error("Failed to fetch blogs:", error);
        }
    };

    useEffect(() => {
        fetchBlogs();
    }, []);

    const handleInput = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e, type) => {
        if (e.target.files[0]) {
            if (type === 'header') setHeaderImage(e.target.files[0]);
            if (type === 'embedded') setEmbeddedImage(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        try {
            let headerUrl = formData.img || "";
            let embeddedUrl = formData.secondaryImg || "";

            if (headerImage) {
                const headerRef = ref(storage, `blogsimage/${Date.now()}_${headerImage.name}`);
                await uploadBytes(headerRef, headerImage);
                headerUrl = await getDownloadURL(headerRef);
            }

            if (embeddedImage) {
                const embeddedRef = ref(storage, `blogsimage/${Date.now()}_${embeddedImage.name}`);
                await uploadBytes(embeddedRef, embeddedImage);
                embeddedUrl = await getDownloadURL(embeddedRef);
            }

            // Create a unique numeric ID for the blog to match existing logic if not editing
            const numericId = editingId ? formData.id : Date.now();

            const blogData = {
                id: numericId, // numeric ID used by UI
                title: formData.title,
                category: formData.category,
                author: formData.author,
                date: formData.date,
                content: formData.content,
                img: headerUrl,
                secondaryImg: embeddedUrl,
                updatedAt: serverTimestamp()
            };

            if (!editingId) {
                blogData.createdAt = serverTimestamp();
                await addDoc(collection(db, "blogs"), blogData);
                setMessage("Blog successfully added!");
            } else {
                await setDoc(doc(db, "blogs", editingId), blogData, { merge: true });
                setMessage("Blog successfully updated!");
            }
            
            cancelEdit();
            fetchBlogs(); 
        } catch (error) {
            console.error("Error saving blog:", error);
            setMessage("Failed to save blog: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (b) => {
        setEditingId(b.dbId);
        setFormData({
            id: b.id,
            title: b.title || "",
            category: b.category || "Being You",
            author: b.author || "",
            date: b.date || "",
            content: b.content || "",
            img: b.img || "",
            secondaryImg: b.secondaryImg || ""
        });
        setHeaderImage(null);
        setEmbeddedImage(null);
        setMessage("Editing blog: " + b.title);
    };

    const handleDelete = async (b) => {
        if (!window.confirm("Are you sure you want to delete '" + b.title + "'?")) return;
        try {
            await deleteDoc(doc(db, "blogs", b.dbId));
            setMessage("Blog deleted.");
            fetchBlogs();
        } catch (error) {
            console.error("Error deleting:", error);
            setMessage("Failed to delete.");
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setFormData({ title: "", category: "Being You", author: "", date: "", content: "", img: "", secondaryImg: "" });
        setHeaderImage(null);
        setEmbeddedImage(null);
        setMessage("");
    };

    const styles = {
        container: { padding: '40px', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Syne', sans-serif" },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
        title: { fontSize: '2.5rem', fontWeight: '400', color: '#003049', margin: 0 },
        mainFlex: { display: 'flex', gap: '40px', alignItems: 'flex-start', flexWrap: 'wrap' },
        formSection: { flex: 1, minWidth: '400px', background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' },
        listSection: { flex: 1, minWidth: '400px', padding: '10px', height: '800px', overflowY: 'auto' },
        inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' },
        label: { fontSize: '14px', fontWeight: '600', color: '#003049' },
        input: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: "'Syne', sans-serif", outline: 'none' },
        textarea: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: "'Syne', sans-serif", outline: 'none', minHeight: '150px', resize: 'vertical' },
        submitBtn: { padding: '15px', background: '#F07167', color: 'white', borderRadius: '30px', cursor: 'pointer', border: 'none', fontWeight: 'bold', fontSize: '16px', width: '100%', marginTop: '10px', boxShadow: '0 4px 15px rgba(240,113,103,0.3)' },
        msg: { padding: '15px', background: '#eefbf3', color: '#155724', borderRadius: '8px', marginBottom: '20px', border: '1px solid #c8e6c9', fontSize: '14px' },
        errorMsg: { padding: '15px', background: '#faeced', color: '#721c24', borderRadius: '8px', marginBottom: '20px', border: '1px solid #f5c6cb', fontSize: '14px' },
        listTitle: { fontSize: '1.5rem', fontWeight: '400', color: '#003049', marginBottom: '20px' },
        blogCard: { padding: '20px', border: 'none', borderRadius: '15px', marginBottom: '15px', background: 'white', display: 'flex', gap: '20px', alignItems: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', position: 'relative' },
        blogImg: { width: '80px', height: '60px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #eee' },
        blogInfo: { flex: 1 },
        blogName: { margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: '#003049', paddingRight: '60px' },
        blogDetails: { margin: '0', fontSize: '13px', color: '#666', lineHeight: '1.6' },
        cardActions: { position: 'absolute', top: '20px', right: '20px', display: 'flex', flexDirection: 'row', gap: '10px' },
        actionBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#003049' },
        deleteBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#F07167' }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>{editingId ? "Edit Blog" : "Add New Blog"}</h1>
            </div>

            <div style={styles.mainFlex}>
                {/* FORM */}
                <div style={styles.formSection}>
                    {message && (
                        <div style={message.includes("Failed") ? styles.errorMsg : styles.msg}>
                            {message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Title</label>
                            <input name="title" value={formData.title} onChange={handleInput} style={styles.input} required placeholder="Blog Title" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Category</label>
                            <select name="category" value={formData.category} onChange={handleInput} style={styles.input} required>
                                <option value="Being You">Being You</option>
                                <option value="Spring of You">Spring of You</option>
                                <option value="Summer of You">Summer of You</option>
                                <option value="Winter of You">Winter of You</option>
                                <option value="Autumn of You">Autumn of You</option>
                            </select>
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Author</label>
                            <input name="author" value={formData.author} onChange={handleInput} style={styles.input} required placeholder="A blog by Meghna Warier" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Date Display (e.g. April 15, 2026)</label>
                            <input name="date" value={formData.date} onChange={handleInput} style={styles.input} required placeholder="April 15, 2026" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Header Image</label>
                            <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'header')} style={styles.input} required={!editingId && !formData.img} />
                            {formData.img && <small style={{color: '#666'}}>Current: {formData.img.split('/').pop().substring(0, 20)}...</small>}
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Embedded Image (Optional but recommended)</label>
                            <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'embedded')} style={styles.input} />
                            {formData.secondaryImg && <small style={{color: '#666'}}>Current: {formData.secondaryImg.split('/').pop().substring(0, 20)}...</small>}
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Content (Use double newlines for paragraphs)</label>
                            <textarea name="content" value={formData.content} onChange={handleInput} style={styles.textarea} required placeholder="Write your blog content here..." />
                        </div>

                        <div style={{display: 'flex', gap: '10px'}}>
                            <button type="submit" style={styles.submitBtn} disabled={loading}>
                                {loading ? "Saving..." : (editingId ? "Update Blog" : "Add Blog")}
                            </button>
                            {editingId && (
                                <button type="button" onClick={cancelEdit} style={{...styles.submitBtn, background: '#eee', color: '#333', boxShadow: 'none'}}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {/* LIST */}
                <div className="no-scrollbar" style={styles.listSection}>
                    <h2 style={styles.listTitle}>Live Blogs ({blogs.length})</h2>
                    {blogs.length === 0 ? (
                        <p style={{ fontSize: '14px', color: '#777' }}>No dynamic blogs found.</p>
                    ) : (
                        blogs.map(b => (
                            <div key={b.dbId} style={styles.blogCard}>
                                <img src={b.img || "/placeholder.jpg"} alt={b.title} style={styles.blogImg} />
                                <div style={styles.blogInfo}>
                                    <h3 style={styles.blogName}>{b.title}</h3>
                                    <p style={styles.blogDetails}>
                                        <strong>Category:</strong> {b.category} <br />
                                        <strong>Author:</strong> {b.author} <br />
                                        <strong>Date:</strong> {b.date}
                                    </p>
                                </div>
                                <div style={styles.cardActions}>
                                    <button onClick={() => handleEdit(b)} style={styles.actionBtn} title="Edit Blog">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 20h9"></path>
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                        </svg>
                                    </button>
                                    <button onClick={() => handleDelete(b)} style={styles.deleteBtn} title="Delete Blog">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
